import { checkSubscriberStatus } from 'api/auth';
import LoginForm from 'components/authentication/LoginForm';
import MainLayout from 'components/layout/MainLayout';
import PageTitle from 'components/PageTitle';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { NextSeo } from 'next-seo';
import Head from 'next/head';
import Link from 'next/link';
import Router, { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { isSafeRedirect } from 'utils/url/isSafeRedirect';

const Login = observer(() => {
  const { displayToast, navigateToDashboard } = useUIStore();
  const router = useRouter();
  const { currentUser, updateCurrentUser } = useUserStore();
  const isRedirecting = useRef<boolean>(false);

  let redirectUrl =
    typeof router.query.redirect == 'string'
      ? router.query.redirect
      : router.query.redirect?.[0];

  useEffect(() => {
    if (currentUser && !isRedirecting.current) {
      if (redirectUrl && isSafeRedirect(redirectUrl)) {
        if (!redirectUrl.includes('admin')) {
          isRedirecting.current = true;
          console.log('redirecting now to ', redirectUrl);
          router.replace(redirectUrl);
        }
      } else {
        isRedirecting.current = true;
        navigateToDashboard();
      }
    }
  }, [redirectUrl, currentUser, isRedirecting.current]);

  useEffect(() => {
    // Show a toast letting people know that they need to log in. However, don't show the toast:
    // - If they're just going back to the homepage (reason unknown)
    // - If there is a promocode (pc) query-string parameter. This is so that landing pages for
    //   promo codes that are player-specific are not overwhelming
    if (redirectUrl && redirectUrl !== '/' && redirectUrl.indexOf('pc=') < 0) {
      displayToast({
        title: 'You need to login to view that page',
        color: '#FF6600',
        timeout: 5000,
      });
    }
  }, [redirectUrl]);

  return (
    <MainLayout additionalClassName="login">
      <Head>
        <meta name="robots" content="noindex" />
      </Head>
      <NextSeo
        title="Log in | Win Epic Prizes | DAYMADE"
        description="Log in to your DAYMADE account here"
        openGraph={{
          title: 'Log in | Win Epic Prizes | DAYMADE',
          description: 'Log in to your DAYMADE account here',
        }}
      />
      <PageTitle>Welcome</PageTitle>
      <div className="loginContent fullWidth flexCentered">
        <p style={{ margin: '1em 0 0', textAlign: 'center' }}>
          Don't have an account yet or signed up with a guest account?
        </p>

        <Link
          href={redirectUrl ? `/register?redirect=${redirectUrl}` : '/register'}
          style={{ textDecoration: 'underline' }}>
          Create an account
        </Link>

        <div className="paddedMaxWidthContainer">
          <LoginForm
            submitButtonTitle={
              redirectUrl === 'checkout' ? 'Continue to payment' : undefined
            }
            completionHandler={async (token) => {
              if (!isRedirecting.current) {
                if (redirectUrl === null || typeof redirectUrl !== 'string') {
                  const { subscriberStatus } =
                    await checkSubscriberStatus(token);

                  // If no redirect path specified, redirect subscribers to home page and non-subscribers to enter page.
                  redirectUrl = subscriberStatus ? '?welcome' : 'enter';
                }
                isRedirecting.current = true;
                Router.replace(`/${redirectUrl}`);
              }
              await updateCurrentUser();
            }}
            appleRedirectPath={router.pathname}
          />
        </div>
      </div>
      <style jsx>
        {`
          .loginContent {
            padding: 4em 0 3em;
          }

          :global(.login .subheading) {
            margin-bottom: 0.5em;
          }
        `}
      </style>
    </MainLayout>
  );
});

export default Login;
